import { Theme, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import React, { useRef, useEffect } from 'react';

import { Colors } from 'src/common/ui/base';
import {
  CHART_ELEMENT_V2,
  RENDER_CHART_ELEMENT_MENU,
  RENDER_PSS_BUTTONS,
  USE_DATO_SHORTCUT_ENABLED_CHART_ELEMENTS,
} from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { ChartElementAlt } from 'src/nightingale/components/ChartElement/ChartElement.Alt';
import { ChartElementConfirm } from 'src/nightingale/components/ChartElement/ChartElement.Confirm';
import { ChartElementEditor } from 'src/nightingale/components/ChartElement/ChartElement.Editor';
import { ChartElementErrorBoundary } from 'src/nightingale/components/ChartElement/ChartElement.ErrorBoundary';
import { ChartElementSnackbar } from 'src/nightingale/components/ChartElement/ChartElement.SnackbarError';
import { ChartElementView } from 'src/nightingale/components/ChartElement/ChartElement.View';
import { getMessageForError } from 'src/nightingale/components/ChartElement/ChartElement.utils';
import { shortcutSupportedFlows as hardcodedShortcutSupportedFlows } from 'src/nightingale/components/ChartElement/shortcutSupportedFlows';
import { ChartElementProps } from 'src/nightingale/components/ChartElement/types';
import { useChartElement } from 'src/nightingale/components/ChartElement/useChartElement';

const ChartElementV1: React.FC<ChartElementProps> = props => (
  <ChartElementErrorBoundary definition={props.definition}>
    <ChartElementInner {...props} />
  </ChartElementErrorBoundary>
);

const ChartElementInner: React.FC<ChartElementProps> = ({
  definition,
  onEdit,
  onView,
  setActiveElement,
  clearActiveElement,
  flowName,
  hideFlow,
  shortcutEnabledChartElements,
}) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    clearSnackbarError,
    clearChartElement,
    confirmChartElement,
    definitionWithValues,
    enterChartElement,
    focusChartElement,
    hasValidationError,
    isReadOnly,
    isSaving,
    isFlagged,
    hasWarning,
    leaveChartElement,
    saveError,
    setPropertyValue,
    setToEditMode,
    setValidationErrorForProperty,
    snackbarError,
    startEditing,
    uiMode,
    holdClearActiveElement,
  } = useChartElement({
    containerRef,
    definition,
    onEdit,
    onView,
  });

  const renderChartElementMenuEnabled = useFeatureFlag(RENDER_CHART_ELEMENT_MENU);
  const renderPssButtonsEnabled = useFeatureFlag(RENDER_PSS_BUTTONS);
  const useDatoShortcutEnabledChartElements = useFeatureFlag(
    USE_DATO_SHORTCUT_ENABLED_CHART_ELEMENTS,
  );

  const styles = useStyles();
  const viewerClassNames = [styles.viewer];
  if (isFlagged) {
    viewerClassNames.push(styles.isFlagged);
  } else if (hasWarning) {
    viewerClassNames.push(styles.hasWarning);
  }

  useEffect(() => {
    // if we clear the active element too soon the save call won't go out
    if (clearActiveElement && !holdClearActiveElement) {
      clearActiveElement();
    }
  }, [clearActiveElement, holdClearActiveElement]);

  // are shortcut buttons enabled for this chart element
  const isShortcutEnabled =
    definition.id && shortcutEnabledChartElements
      ? shortcutEnabledChartElements.includes(definition.id)
      : false;

  // deprecated logic
  const isHardcodedShortcutEnabled = flowName
    ? hardcodedShortcutSupportedFlows.includes(flowName)
    : false;

  // use hardcoded flows if the feature flag is not enabled
  const isShortcutSupported = useDatoShortcutEnabledChartElements
    ? isShortcutEnabled
    : isHardcodedShortcutEnabled;

  // hardcoded was based on flowname
  // the new incoming list is based on the chart element id

  // ffs for both locations that shortcuts can be rendered
  return (
    <section
      aria-selected={uiMode !== 'view'}
      className={styles.container}
      data-chart-item-type="ChartElement"
      data-chart-item-name={definition.name}
      data-testid="chart-element-container"
      data-test-name={definitionWithValues.name}
      onBlur={event => {
        const { currentTarget } = event;

        // @src https://muffinman.io/blog/catching-the-blur-event-on-an-element-and-its-children/
        // Give browser time to focus the next element
        requestAnimationFrame(() => {
          // Check if the new focused element is a child of the original container
          if (!currentTarget.contains(document.activeElement)) {
            leaveChartElement();
          }
        });
      }}
      onFocus={event => {
        const { currentTarget } = event;
        requestAnimationFrame(() => {
          if (viewerRef?.current && (childHasFocus(viewerRef.current) || hasFocus(currentTarget))) {
            enterChartElement();
            if (setActiveElement) setActiveElement(definition.id);
          }
        });
      }}
      ref={containerRef}
      role="tab"
      tabIndex={isReadOnly ? undefined : 0}
    >
      <ChartElementSnackbar error={snackbarError} clearError={clearSnackbarError} />
      {uiMode === 'edit' && (
        <div data-testid="chart-element-editor-container" className={styles.editor} key="editor">
          <ChartElementEditor
            definition={definitionWithValues}
            focusChartElement={focusChartElement}
            hasSaveError={!!saveError}
            isSaving={isSaving}
            onValidationError={setValidationErrorForProperty}
            saveErrorMessage={getMessageForError(saveError)}
            setPropertyValue={setPropertyValue}
            onClearElement={() => clearChartElement()}
            onHideFlow={hideFlow && (() => hideFlow())}
            isShortcutSupported={renderChartElementMenuEnabled && isShortcutSupported}
          />
        </div>
      )}
      {(uiMode === 'view' || uiMode === 'refresh') && (
        <div
          className={classNames(viewerClassNames)}
          data-testid="chart-element-viewer-container"
          key="viewer"
          ref={viewerRef}
        >
          <ChartElementView
            definition={definitionWithValues}
            hasSaveError={!!saveError}
            hasValidationError={hasValidationError}
            isSaving={isSaving}
            startEditing={startEditing}
          />
        </div>
      )}
      {uiMode === 'confirm' && (
        <div
          className={styles.viewer}
          data-testid="chart-element-viewer-container"
          key="viewer"
          ref={viewerRef}
        >
          <ChartElementConfirm
            definition={definitionWithValues}
            onConfirm={() => confirmChartElement()}
            onEdit={() => setToEditMode()}
            hideFlow={hideFlow}
            clearElement={clearChartElement}
            isShortcutSupported={renderPssButtonsEnabled && isShortcutSupported}
          />
        </div>
      )}
    </section>
  );
};

const useStyles = makeStyles<Theme>(theme => {
  const highlight = (backgroundColor: string, borderColor: string) => ({
    background: backgroundColor,
    border: `${theme.spacing(0.25)}px solid ${borderColor}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
  });

  return {
    container: {
      maxWidth: 600,
      position: 'relative',
      '&:focus': {
        outline: `2px solid ${Colors.Stillwater}`,
      },
    },
    '@keyframes fadeIn': {
      '0%': {
        opacity: 0,
      },
      '100%': {
        opacity: 1,
      },
    },
    isFlagged: highlight(Colors.Red1, Colors.Red2),
    hasWarning: highlight(Colors.Yellow1, Colors.Yellow2),
    viewer: {
      animation: `$fadeIn 500ms ease-out`,
    },
    editor: {
      animation: `$fadeIn 500ms ease-out`,
    },
  };
});

/** Does the given element contain the element that has focus? */
function childHasFocus(element: HTMLElement) {
  return element.contains(document.activeElement);
}

/** Does this element have focus? */
function hasFocus(element: HTMLElement): boolean {
  return element === document.activeElement;
}

export const ChartElement = props => {
  const v2Enabled = useFeatureFlag(CHART_ELEMENT_V2);
  if (v2Enabled) {
    return <ChartElementAlt {...props} />;
  }
  return <ChartElementV1 {...props} />;
};
